'use client'

import { useState } from 'react'
import { SoloViewerTokenFragment } from '@/gql/generated/graphql'
import { BodyText, DetailText } from 'design-configuration/components'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { AllPurposeLink } from '@components/all-purpose-link'
import { tokenShowRoute } from '@utils/routes'
import { createSerializer, parseAsBoolean } from 'nuqs'

const tokenParamsSerializer = createSerializer({
  hideNavigationArrows: parseAsBoolean.withDefault(false)
})

type ShowcaseDisplayGridItemProps = {
  token: SoloViewerTokenFragment
}

export const ShowcaseDisplayGridItem = ({ token }: ShowcaseDisplayGridItemProps): React.ReactElement => {
  const [isHovered, setIsHovered] = useState(false)
  const isRendered = !!token.image_id

  return (
    <AllPurposeLink
      href={tokenParamsSerializer(tokenShowRoute(token.contract_address, token.token_id), {
        hideNavigationArrows: true
      })}
    >
      <div
        className="relative cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative flex w-full flex-col">
          {isRendered ? (
            <div className={`relative aspect-square h-auto`}>
              <div className="relative h-full w-full">
                <ImageWithCustomLoader
                  className="p-sm drop-shadow-[3px_3px_6px_rgba(0,0,0,0.20)]"
                  src={token.media_url ?? ''}
                  blurHash={token.image?.metadata?.blurhash}
                  fill
                  style={{
                    objectFit: 'contain',
                    objectPosition: 'center'
                  }}
                  priority
                  alt={`${token.invocation}`}
                  sizes="33vw"
                />
              </div>
            </div>
          ) : (
            <div className="bg-surface-container gap-y-xs flex h-full min-h-[150px] w-full flex-col items-center justify-center">
              <DetailText className="font-medium">{`Rendering #${token.invocation}`}</DetailText>
            </div>
          )}

          {/* Hover overlay with token info */}
          {isHovered ? (
            <div className="p-xs rounded-xs absolute bottom-0 left-0">
              <div className="bg-surface-90 p-xs gap-2xs rounded-xs flex flex-col">
                <BodyText className="font-medium">
                  {token.project?.name} #{token.invocation}
                </BodyText>
                <BodyText>{token.project?.artist_name}</BodyText>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </AllPurposeLink>
  )
}
